import * as Yup from 'yup';
import { isEmpty } from 'lodash';
import { Feature, Polygon } from 'geojson';

import { Nullable } from '~globals/types';
import { AdminLogisticZone } from '~services/admin/logisticsZones/types';
import { getRandomColor } from '~utils/commons';
import {
  parseRangeDescriptionToArray,
  isLogisticTypeRange,
  isLogisticTypeGeometryArea,
} from '~utils/logisiticsZones';
import { createFeature } from '~utils/geoJson';
import { VALIDATIONS_ERROR } from '~constants/index';

export const FIELDS_NAME = {
  ENABLED: 'enabled',
  CODE: 'code',
  COLOR: 'color',
  DESCRIPTION: 'description',
  ZONE_TYPE_ID: 'zoneTypeId',
  RANGE_DESCRIPTION: 'rangeDescription',
  POLYGON_FEATURE: 'polygonFeature',
} as const;

const defaultPolygonFeature = (
  data?: Nullable<string>,
  color?: string,
): Nullable<Feature<Polygon>> => {
  if (!data) return null;

  const polygon: Polygon = JSON.parse(data);

  return createFeature({
    geometry: polygon,
    properties: { currentColor: color },
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const getInitialValues = (data?: Nullable<AdminLogisticZone>) => ({
  [FIELDS_NAME.ENABLED]: data?.enabled ?? true,
  [FIELDS_NAME.CODE]: data?.code ?? '',
  [FIELDS_NAME.COLOR]: data?.color ?? getRandomColor(),
  [FIELDS_NAME.DESCRIPTION]: data?.description ?? '',
  [FIELDS_NAME.ZONE_TYPE_ID]: data?.zoneTypeId.toString() ?? '',
  [FIELDS_NAME.RANGE_DESCRIPTION]: data?.rangeDescription
    ? parseRangeDescriptionToArray(data.rangeDescription)
    : [],
  [FIELDS_NAME.POLYGON_FEATURE]: defaultPolygonFeature(
    data?.geometryAreaJson,
    data?.color,
  ),
});

export const validationSchema = Yup.object().shape({
  [FIELDS_NAME.ENABLED]: Yup.boolean(),
  [FIELDS_NAME.CODE]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.COLOR]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.DESCRIPTION]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.ZONE_TYPE_ID]: Yup.string().required(VALIDATIONS_ERROR.REQUIRED),
  [FIELDS_NAME.RANGE_DESCRIPTION]: Yup.array()
    .of(Yup.string())
    .when(FIELDS_NAME.ZONE_TYPE_ID, {
      is: (zoneTypeId: string) =>
        !isEmpty(zoneTypeId) && isLogisticTypeRange(Number(zoneTypeId)),
      then: (schema) => schema.min(1, VALIDATIONS_ERROR.REQUIRED),
    }),
  [FIELDS_NAME.POLYGON_FEATURE]: Yup.mixed().when(FIELDS_NAME.ZONE_TYPE_ID, {
    is: (zoneTypeId: string) =>
      !isEmpty(zoneTypeId) && isLogisticTypeGeometryArea(Number(zoneTypeId)),
    then: (schema) => schema.required(VALIDATIONS_ERROR.REQUIRED),
    otherwise: (schema) => schema.nullable(),
  }),
});
